import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface EditStallApplicationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  application: any | null;
  onSuccess: () => void;
}

export function EditStallApplicationDialog({ open, onOpenChange, application, onSuccess }: EditStallApplicationDialogProps) {
  const [businessName, setBusinessName] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [description, setDescription] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [status, setStatus] = useState("pending");
  const [keepForFuture, setKeepForFuture] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (application) {
      setBusinessName(application.business_name || "");
      setContactName(application.contact_name || "");
      setContactEmail(application.contact_email || "");
      setContactPhone(application.contact_phone || "");
      setDescription(application.description || "");
      setPaymentAmount(application.payment_amount?.toString() || "");
      setStatus(application.status || "pending");
      setKeepForFuture(application.keep_for_future || false);
    }
  }, [application, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!businessName.trim()) {
      toast.error("Please enter a business name");
      return;
    }

    if (!contactEmail.trim()) {
      toast.error("Please enter a contact email");
      return;
    }

    setIsSubmitting(true);

    try {
      const updateData = {
        business_name: businessName.trim(),
        contact_name: contactName.trim(),
        contact_email: contactEmail.trim(),
        contact_phone: contactPhone.trim(),
        description: description.trim(),
        payment_amount: paymentAmount ? parseFloat(paymentAmount) : null,
        status,
        keep_for_future: keepForFuture,
      };

      const { error } = await supabase
        .from("stall_applications")
        .update(updateData)
        .eq("id", application.id);

      if (error) throw error;

      // If approved and keep_for_future is true, upsert contact
      if (status === 'approved' && keepForFuture) {
        try {
          // Check if contact already exists
          const { data: existingContact } = await supabase
            .from("stall_contacts")
            .select("id")
            .eq("pta_id", application.pta_id)
            .eq("contact_email", contactEmail.trim())
            .maybeSingle();

          const eventDate = application.events?.event_date?.split('T')[0] || new Date().toISOString().split('T')[0];

          if (existingContact) {
            // Update existing contact
            await supabase
              .from("stall_contacts")
              .update({
                business_name: businessName.trim(),
                contact_name: contactName.trim(),
                contact_phone: contactPhone.trim(),
                description: description.trim(),
                last_stall_date: eventDate,
              })
              .eq("id", existingContact.id);
          } else {
            // Create new contact
            await supabase
              .from("stall_contacts")
              .insert({
                pta_id: application.pta_id,
                business_name: businessName.trim(),
                contact_name: contactName.trim(),
                contact_email: contactEmail.trim(),
                contact_phone: contactPhone.trim(),
                description: description.trim(),
                last_stall_date: eventDate,
              });
          }
        } catch (contactError) {
          console.error("Failed to save contact:", contactError);
        }
      } else if (!keepForFuture) {
        // If keep_for_future is false, optionally remove from contacts
        await supabase
          .from("stall_contacts")
          .delete()
          .eq("pta_id", application.pta_id)
          .eq("contact_email", contactEmail.trim());
      }

      toast.success("Application updated successfully");

      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error("Error updating application:", error);
      toast.error("Failed to update application");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Edit Stall Application</DialogTitle>
            <DialogDescription>
              Update the stall application details
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="businessName">Business Name *</Label>
                <Input
                  id="businessName"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  placeholder="Enter business name"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contactName">Contact Name</Label>
                <Input
                  id="contactName"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  placeholder="Enter contact name"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contactEmail">Contact Email *</Label>
                <Input
                  id="contactEmail"
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="Enter contact email"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contactPhone">Contact Phone</Label>
                <Input
                  id="contactPhone"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  placeholder="Enter contact phone"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe what will be sold..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="paymentAmount">Payment Amount (£)</Label>
                <Input
                  id="paymentAmount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  placeholder="0.00"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="declined">Declined</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center justify-between p-4 border border-border rounded-lg">
              <div className="space-y-0.5">
                <Label htmlFor="keepForFuture">Consent to Store Details</Label>
                <p className="text-sm text-muted-foreground">
                  Business has consented to keep their details for future events
                </p>
              </div>
              <Switch
                id="keepForFuture"
                checked={keepForFuture}
                onCheckedChange={setKeepForFuture}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : "Update"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
